"use client";

import { useEffect, useRef, useState } from "react";
import { ColumnDef, flexRender } from "@tanstack/react-table";

import { cn } from "@/lib/utils";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";

// Reports whether `isLoading` has been true at any point in this component
// instance's lifetime. Use it to gate fade-in animations so they only run
// when a skeleton/loading state was actually shown first — on warm
// navigations (data already cached) content mounts ready and must NOT
// re-fade, which reads as a flash.
export function useSawLoading(isLoading: boolean): boolean {
  const saw = useRef(isLoading);
  if (isLoading) saw.current = true;
  return saw.current;
}

// Wraps content that replaces a skeleton so it fades in on mount instead of
// popping. Mount it in the "ready" branch of the same conditional that
// rendered the skeleton, and pass `enabled` from useSawLoading so warm
// mounts (no skeleton was shown) skip the animation.
export function SkeletonFadeIn({
  className,
  children,
  enabled = true,
}: {
  className?: string;
  children: React.ReactNode;
  enabled?: boolean;
}) {
  return (
    <div
      className={cn(enabled && "duration-300 animate-in fade-in", className)}
    >
      {children}
    </div>
  );
}

// Animates its own height (ease-in-out) when the content inside changes
// size — e.g. a 3-row skeleton swapping to an N-row loaded list. Measures
// the inner content with a ResizeObserver and transitions the outer
// wrapper's explicit height between measurements. Clips only vertically
// (overflow-y-clip) so it can sit inside horizontal-scroll containers
// without breaking their x-overflow.
export function SmoothHeight({
  className,
  children,
}: {
  className?: string;
  children: React.ReactNode;
}) {
  const innerRef = useRef<HTMLDivElement>(null);
  const [height, setHeight] = useState<number | null>(null);

  useEffect(() => {
    const el = innerRef.current;
    if (!el || typeof ResizeObserver === "undefined") return;
    const observer = new ResizeObserver(() => setHeight(el.offsetHeight));
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  return (
    <div
      className="overflow-y-clip transition-[height] duration-300 ease-in-out"
      style={height === null ? undefined : { height }}
    >
      <div ref={innerRef} className={className}>
        {children}
      </div>
    </div>
  );
}

const FADE_OPACITIES = [1, 0.6, 0.3];

const fadeAt = (index: number) =>
  FADE_OPACITIES[Math.min(index, FADE_OPACITIES.length - 1)];

// Varying mock text widths per row, cycled deterministically like CELL_WIDTHS.
const LIST_TITLE_WIDTHS = ["w-40", "w-28", "w-48", "w-32", "w-44"];
const LIST_SUBTITLE_WIDTHS = ["w-56", "w-36", "w-64", "w-44", "w-52"];

// Row internals mirror the real list rows (members/organizations) exactly:
// the two text bars sit inside containers with the true line-box heights
// (title text-sm/sm:text-base = h-5/sm:h-6, subtitle text-xs/sm:text-sm =
// h-4/sm:h-5, no gap) and the trailing bar matches the h-8 sm:h-9 action
// button, so skeleton rows are height-identical to loaded rows.
export function ListRowsSkeleton({
  rows = 3,
  className,
}: {
  rows?: number;
  className?: string;
}) {
  return (
    <div
      data-testid="list-rows-skeleton"
      className={cn("flex flex-col gap-4", className)}
    >
      {Array.from({ length: rows }).map((_, i) => (
        <div
          key={i}
          style={{ opacity: fadeAt(i) }}
          className="flex min-h-14 flex-row items-center justify-between gap-2 rounded-sm border border-transparent bg-background p-2 sm:min-h-16 sm:gap-3 sm:p-3"
        >
          <div className="flex min-w-0 flex-1 flex-row items-center gap-2 sm:gap-3">
            <Skeleton className="h-8 w-8 flex-shrink-0 rounded-full bg-card sm:h-[34px] sm:w-[34px]" />
            <div className="flex min-w-0 flex-col">
              <div className="flex h-5 items-center sm:h-6">
                <Skeleton
                  className={`h-4 bg-card ${LIST_TITLE_WIDTHS[i % LIST_TITLE_WIDTHS.length]}`}
                />
              </div>
              <div className="flex h-4 items-center sm:h-5">
                <Skeleton
                  className={`h-3 bg-card ${LIST_SUBTITLE_WIDTHS[i % LIST_SUBTITLE_WIDTHS.length]}`}
                />
              </div>
            </div>
          </div>
          <Skeleton className="h-8 w-20 shrink-0 bg-card sm:h-9" />
        </div>
      ))}
    </div>
  );
}

// Deterministic width cycle so mock rows vary like real data (names, emails,
// dates of different lengths) without hydration-unsafe randomness.
const CELL_WIDTHS = ["w-36", "w-24", "w-44", "w-28", "w-32", "w-20"];

// One TableSkeleton row is 49px: py-4 cell padding (32) + h-4 bar (16) +
// 1px row border. Empty-state placeholders rendered in a DataTable's
// noResults slot should use this min-height (paired with a p-0 cell) so the
// skeleton→empty swap is height-identical: 5 rows × 49px − 1px row border.
export const TABLE_SKELETON_BODY_MIN_H = "min-h-[244px]";

export function TableSkeleton({
  columns,
  rows = 5,
  containerClassName,
  headerClassName,
}: {
  columns: ColumnDef<any, any>[];
  rows?: number;
  /** Forwarded to Table's scroll wrapper — see Table's containerClassName. */
  containerClassName?: string;
  /** Merged onto TableHeader, e.g. to move its full-bleed top border. */
  headerClassName?: string;
}) {
  const hasExplicitSizes =
    columns.length > 0 && columns.every((c) => c.size != null);

  return (
    <div data-testid="table-skeleton">
      <Table
        className={hasExplicitSizes ? "table-fixed" : undefined}
        containerClassName={containerClassName}
      >
        {hasExplicitSizes && (
          <colgroup>
            {columns.map((c, i) => (
              <col key={i} style={{ width: `${c.size}%` }} />
            ))}
          </colgroup>
        )}
        <TableHeader className={cn("bg-card", headerClassName)}>
          <TableRow>
            {columns.map((col, i) => (
              <TableHead key={i} className="px-5 text-sm font-semibold">
                {flexRender(col.header, {} as any)}
              </TableHead>
            ))}
          </TableRow>
        </TableHeader>
        <TableBody>
          {Array.from({ length: rows }).map((_, r) => (
            <TableRow
              key={r}
              style={{ opacity: Math.round((1 - r / rows) * 100) / 100 }}
            >
              {columns.map((col, c) => (
                <TableCell
                  key={c}
                  className={col.meta?.skeleton ? "px-5" : "px-5 py-4"}
                >
                  {col.meta?.skeleton ?? (
                    <Skeleton
                      className={`h-4 max-w-full bg-backgroundAccent ${CELL_WIDTHS[(r * columns.length + c) % CELL_WIDTHS.length]}`}
                    />
                  )}
                </TableCell>
              ))}
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </div>
  );
}
